import BigNumber from 'bignumber.js';
import { EqualIcon, XIcon, ZapIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

export interface WeightItem {
    ticker: string;
    name: string;
    weight: number;
}

interface StrategyWeightInputProps {
    weights: WeightItem[];
    onWeightChange: (ticker: string, weight: number) => void;
    onRemove: (ticker: string) => void;
}

const StrategyWeightInput = observer(
    ({ weights, onWeightChange, onRemove }: StrategyWeightInputProps) => {
        // BigNumber로 정확한 합계 계산
        const totalWeight = weights
            .reduce((sum, item) => sum.plus(item.weight), new BigNumber(0))
            .toNumber();

        // 드래그 상태 관리
        const dragState = React.useRef<{
            ticker: string;
            startX: number;
            startY: number;
            startWeight: number;
            isDragging: boolean;
            sliderWidth: number;
            precisionMode: boolean;
            precisionTimer?: NodeJS.Timeout;
        } | null>(null);

        // 드래깅 중인지 여부
        const [isDragging, setIsDragging] = React.useState(false);
        // 정밀 모드 상태 (리렌더링용)
        const [precisionTicker, setPrecisionTicker] = React.useState<string | null>(null);
        // '100%로 맞추기' 버튼을 눌렀는지 여부 (동일 비중 모드 전환용)
        const [hasAdjustedToFull, setHasAdjustedToFull] = React.useState(false);

        const handleAdjustWeight = (ticker: string) => {
            const item = weights.find((w) => w.ticker === ticker);
            if (!item) return;

            // BigNumber로 정확한 차이 계산
            const diff = new BigNumber(1).minus(totalWeight);
            const newWeight = BigNumber.max(
                0,
                BigNumber.min(1, new BigNumber(item.weight).plus(diff)),
            ).toNumber();
            onWeightChange(ticker, newWeight);
        };

        // 동일 비중으로 분배 (남는 비중은 현금으로)
        const handleEqualWeight = () => {
            if (weights.length === 0) return;

            // BigNumber로 정확한 동일 분배 계산 (1% 단위로 버림)
            const equalWeight = new BigNumber(1)
                .dividedBy(weights.length)
                .multipliedBy(100)
                .integerValue(BigNumber.ROUND_FLOOR)
                .dividedBy(100)
                .toNumber();

            weights.forEach((item) => {
                onWeightChange(item.ticker, equalWeight);
            });
            // 남는 비중은 자동으로 현금 비중이 됨
        };

        // 100%로 맞추기 (1% 단위로, 부족분은 첫 번째 종목에)
        const handleProportionalAdjust = () => {
            if (weights.length === 0) return;

            // 모든 비중이 0이면 동일 분배
            if (totalWeight === 0) {
                handleEqualWeight();
                return;
            }

            // 0%가 아닌 종목들만 필터링
            const nonZeroWeights = weights.filter((item) => item.weight > 0);
            const zeroWeights = weights.filter((item) => item.weight === 0);

            if (nonZeroWeights.length === 0) {
                // 모든 비중이 0이면 동일 분배
                handleEqualWeight();
                return;
            }

            // BigNumber로 정확한 합계 계산
            const nonZeroTotal = nonZeroWeights
                .reduce((sum, item) => sum.plus(item.weight), new BigNumber(0))
                .toNumber();

            // 0%가 아닌 종목들을 100%로 비례 조정 (1% 단위로 내림)
            const ratio = new BigNumber(1).dividedBy(nonZeroTotal);
            const adjustedWeights = nonZeroWeights.map((item) => ({
                ticker: item.ticker,
                weight: new BigNumber(item.weight)
                    .multipliedBy(ratio)
                    .multipliedBy(100)
                    .integerValue(BigNumber.ROUND_FLOOR)
                    .dividedBy(100)
                    .toNumber(),
            }));

            // BigNumber로 내림 후 합계 계산
            const sumAfterFloor = adjustedWeights
                .reduce((sum, w) => sum.plus(w.weight), new BigNumber(0))
                .toNumber();

            // 부족한 비중은 첫 번째 종목에 추가
            if (sumAfterFloor < 1 && adjustedWeights.length > 0) {
                const shortage = new BigNumber(1).minus(sumAfterFloor);
                adjustedWeights[0].weight = new BigNumber(adjustedWeights[0].weight)
                    .plus(shortage)
                    .multipliedBy(100)
                    .integerValue(BigNumber.ROUND_HALF_UP)
                    .dividedBy(100)
                    .toNumber();
            }

            // 조정된 비중 적용
            adjustedWeights.forEach((w) => {
                onWeightChange(w.ticker, w.weight);
            });

            // 0% 종목들은 0% 유지
            zeroWeights.forEach((w) => {
                onWeightChange(w.ticker, 0);
            });
        };

        // 비중이 동일한지 체크 (1% 단위 기준)
        const areWeightsEqual = () => {
            if (weights.length === 0) return true;
            const firstWeight = Math.round(weights[0].weight * 100);
            return weights.every((w) => Math.round(w.weight * 100) === firstWeight);
        };

        // 버튼 상태 결정
        const getAdjustButtonState = () => {
            if (weights.length === 0) {
                return { disabled: true, mode: 'proportional' as const };
            }

            // 총 비중이 100%인 경우
            if (totalWeight === 1) {
                // 동일 비중이면 버튼 비활성화
                if (areWeightsEqual()) {
                    return { disabled: true, mode: 'proportional' as const };
                }
                // '100%로 맞추기'를 누른 적이 있고 동일 비중이 아니면 '동일 비중으로' 모드
                if (hasAdjustedToFull) {
                    return { disabled: false, mode: 'equal' as const };
                }
                // 그 외에는 '동일 비중으로' 모드
                return { disabled: false, mode: 'equal' as const };
            }

            // 총 비중이 100% 아니면 항상 "100%로 맞추기" 활성화
            return {
                disabled: false,
                mode: 'proportional' as const,
            };
        };

        const buttonState = getAdjustButtonState();

        const handlePointerDown = (e: React.PointerEvent, ticker: string) => {
            const item = weights.find((w) => w.ticker === ticker);
            if (!item) return;

            const rect = e.currentTarget.getBoundingClientRect();

            // 모든 경우에 드래그 준비 (기존 동작 유지)
            e.currentTarget.setPointerCapture(e.pointerId);

            // 조정 후의 최신 비중을 가져오기 위해 다시 검색
            const updatedItem = weights.find((w) => w.ticker === ticker);

            // 정밀 모드 타이머 설정 (1초 후 활성화)
            const precisionTimer = setTimeout(() => {
                if (dragState.current && dragState.current.ticker === ticker) {
                    // 정밀 모드 진입 시점의 상태를 새로운 시작점으로 설정 (튐 방지)
                    const currentItem = weights.find((w) => w.ticker === ticker);
                    if (currentItem) {
                        dragState.current.startWeight = currentItem.weight;
                        dragState.current.startX =
                            dragState.current.startX +
                            (currentItem.weight - dragState.current.startWeight) *
                                dragState.current.sliderWidth;
                    }

                    dragState.current.precisionMode = true;
                    setPrecisionTicker(ticker);
                }
            }, 500);

            dragState.current = {
                ticker,
                startX: e.clientX,
                startY: e.clientY,
                startWeight: updatedItem?.weight || item.weight,
                isDragging: false,
                sliderWidth: rect.width,
                precisionMode: false,
                precisionTimer,
            };
        };

        const handlePointerMove = (e: React.PointerEvent) => {
            if (!dragState.current) return;
            if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;

            const {
                startX,
                startY,
                startWeight,
                sliderWidth,
                precisionMode,
                precisionTimer,
                ticker,
            } = dragState.current;

            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;

            const isMoved = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5;
            const isMovedX = Math.abs(deltaX) > 5 && Math.abs(deltaY) < 5;

            // 움직이면 일단 정밀 모드 타이머 취소
            if (isMoved) {
                // 처음 드래그를 시작하면 정밀 모드 타이머 취소
                if (precisionTimer) {
                    clearTimeout(precisionTimer);
                    dragState.current.precisionTimer = undefined;
                }
            }

            // X축 움직임 감지
            if (!dragState.current.isDragging && isMovedX) {
                dragState.current.isDragging = true;
                setIsDragging(true);
            }

            if (dragState.current.isDragging || precisionMode) {
                // 드래그 중에는 기본 동작 방지 (스크롤 등)
                e.preventDefault();

                // 기존 정밀 모드 타이머 취소 (움직임이 있으면 리셋)
                if (dragState.current.precisionTimer) {
                    clearTimeout(dragState.current.precisionTimer);
                }

                // 드래그 중 멈춤 감지: 1초 후 정밀 모드 활성화
                if (!precisionMode) {
                    dragState.current.precisionTimer = setTimeout(() => {
                        if (dragState.current && dragState.current.ticker === ticker) {
                            // 정밀 모드 진입 시점의 상태를 새로운 시작점으로 설정 (튐 방지)
                            const currentItem = weights.find((w) => w.ticker === ticker);
                            if (currentItem) {
                                dragState.current.startWeight = currentItem.weight;
                                dragState.current.startX = e.clientX;
                            }

                            dragState.current.precisionMode = true;
                            setPrecisionTicker(ticker);
                        }
                    }, 500);
                }

                // BigNumber로 정확한 비중 변경 계산
                let deltaRatio = new BigNumber(deltaX).dividedBy(sliderWidth);

                // 정밀 모드: 민감도 3배 증가 (같은 거리를 움직여도 1/3만 변경)
                if (precisionMode) {
                    deltaRatio = deltaRatio.dividedBy(3);
                }

                let newWeight = BigNumber.max(
                    0,
                    BigNumber.min(1, new BigNumber(startWeight).plus(deltaRatio)),
                ).toNumber();

                // 스냅 적용: 부드럽게 가까운 값으로 자석처럼 끌어당기기
                if (!precisionMode) {
                    // 5% 단위 스냅: 가까운 5% 배수로 부드럽게 이동
                    const snapUnit = 0.05;
                    const snappedValue = Math.round(newWeight / snapUnit) * snapUnit;
                    const distance = Math.abs(newWeight - snappedValue);

                    // 1% 이내에 있으면 스냅 (자석 효과)
                    if (distance <= 0.01) {
                        newWeight = snappedValue;
                    }
                } else {
                    // 정밀 모드: 1% 단위 스냅
                    const snapUnit = 0.01;
                    const snappedValue = Math.round(newWeight / snapUnit) * snapUnit;
                    const distance = Math.abs(newWeight - snappedValue);

                    // 0.3% 이내에 있으면 스냅
                    if (distance <= 0.003) {
                        newWeight = snappedValue;
                    }
                }

                onWeightChange(dragState.current.ticker, newWeight);
            }
        };

        const handlePointerUp = (e: React.PointerEvent) => {
            // 정밀 모드 타이머 정리
            if (dragState.current?.precisionTimer) {
                clearTimeout(dragState.current.precisionTimer);
            }

            setPrecisionTicker(null);
            setIsDragging(false);
            dragState.current = null;

            // capture 즉시 해제 (더블탭 지원)
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
        };

        // weights가 변경되면 hasAdjustedToFull 상태 리셋
        useEffect(() => {
            setHasAdjustedToFull(false);
        }, [weights]);

        useEffect(() => {
            const onTouchMove = (e: TouchEvent) => {
                e.preventDefault();
            };

            if (isDragging || precisionTicker) {
                window.addEventListener('touchmove', onTouchMove, { passive: false });
            }

            return () => {
                window.removeEventListener('touchmove', onTouchMove);
            };
        }, [isDragging, precisionTicker]);

        if (weights.length === 0) {
            return (
                <div className="text-base-content/60 flex h-32 items-center justify-center text-sm">
                    종목을 추가해주세요
                </div>
            );
        }

        return (
            <div className="flex flex-col select-none" data-ignore-pull-to-refresh={true}>
                <div className="flex flex-col gap-4">
                    {weights.map((item) => {
                        const isPrecisionMode = precisionTicker === item.ticker;
                        const weightPercentage = Math.round(item.weight * 100);

                        return (
                            <div
                                key={item.ticker}
                                className="border-base-300 flex gap-2 border-b pb-5 last:border-b-0 last:pb-0"
                            >
                                {/* 좌측: 상단행 + 슬라이더 영역 (투명 input이 여기를 덮음) */}
                                <div className="relative flex flex-1 flex-col gap-1.5">
                                    {/* 상단 행: 종목명 + 비중 (pointer-events-none) */}
                                    <div className="pointer-events-none flex items-center justify-between gap-2">
                                        {/* 좌측: 종목 정보 */}
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-base font-bold">
                                                {item.ticker}
                                            </span>
                                            <span className="text-base-content/60 text-sm">
                                                {item.name}
                                            </span>
                                        </div>

                                        {/* 우측: 비중 */}
                                        <span className="text-xl font-semibold tabular-nums">
                                            {weightPercentage}%
                                        </span>
                                    </div>

                                    {/* 하단: 시각적 슬라이더 wrapper */}
                                    <div className="relative my-0.5 leading-0">
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={weightPercentage}
                                            className="range range-sm pointer-events-none w-full"
                                            readOnly
                                            tabIndex={-1}
                                        />
                                        {/* 핸들 위치에 상세 조정 원형 표시 */}
                                        <div className="absolute inset-0 mx-[10.25px]">
                                            <div
                                                className={twMerge(
                                                    'border-base-content bg-base-100/0 absolute top-1/2 z-[1] size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border backdrop-invert',
                                                    isPrecisionMode && 'size-10 opacity-100',
                                                )}
                                                style={{
                                                    transition:
                                                        'opacity 0.1s ease-in-out, width 0.1s ease-in-out, height 0.1s ease-in-out',
                                                    left: `${weightPercentage}%`,
                                                    opacity: isPrecisionMode ? 1 : 0,
                                                }}
                                            ></div>
                                        </div>
                                    </div>

                                    {/* 투명한 터치 영역: pointer 이벤트로 처리 */}
                                    <div
                                        className="absolute inset-0 z-[1] cursor-pointer"
                                        onPointerDown={(e) => handlePointerDown(e, item.ticker)}
                                        onPointerMove={handlePointerMove}
                                        onPointerUp={handlePointerUp}
                                    />
                                </div>

                                {/* 우측: 버튼 영역 (삭제 + 맞추기) */}
                                <div className="flex flex-shrink-0 flex-col items-center justify-between">
                                    <button
                                        className="btn btn-ghost btn-xs btn-circle opacity-40 hover:opacity-100"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onRemove(item.ticker);
                                        }}
                                    >
                                        <XIcon className="size-4" />
                                    </button>
                                    <button
                                        className="btn btn-ghost btn-xs btn-circle opacity-40 hover:opacity-100"
                                        disabled={totalWeight === 1}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleAdjustWeight(item.ticker);
                                        }}
                                    >
                                        <ZapIcon className="size-4" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 총 비중 표시 */}
                <div className="bg-base-200 -mx-5 mt-6 px-5 py-5">
                    <div className="flex items-center justify-between">
                        <span className="font-semibold">총 비중</span>
                        <span
                            className={
                                totalWeight > 1
                                    ? 'text-error text-lg font-bold'
                                    : 'text-lg font-bold'
                            }
                        >
                            {(totalWeight * 100).toFixed(0)}%
                        </span>
                    </div>

                    {/* 항상 표시 */}
                    <div className="mt-2 flex items-center justify-between text-sm">
                        <span className="text-base-content/70">종목 비중</span>
                        <span>{(totalWeight * 100).toFixed(0)}%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-base-content/70">현금 비중</span>
                        <span>{Math.max(0, (1 - totalWeight) * 100).toFixed(0)}%</span>
                    </div>

                    <div className="mt-2 flex flex-row items-center justify-between gap-2">
                        <div className="text-error text-xs">
                            {totalWeight > 1 && <span>총 비중을 100%에 맞춰주세요</span>}
                        </div>
                        {/* 상황에 따라 다른 버튼 표시 */}
                        <button
                            className="btn btn-soft btn-sm w-33"
                            disabled={buttonState.disabled}
                            onClick={(e) => {
                                e.preventDefault();
                                if (buttonState.mode === 'equal') {
                                    handleEqualWeight();
                                } else {
                                    handleProportionalAdjust();
                                    setHasAdjustedToFull(true);
                                }
                            }}
                        >
                            {buttonState.mode === 'equal' ? (
                                <>
                                    <EqualIcon className="size-4" />
                                    동일 비중으로
                                </>
                            ) : (
                                <>
                                    <ZapIcon className="size-4" />
                                    100%로 맞추기
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    },
);

export default StrategyWeightInput;
